import ApiService from './apiService';

export interface SiteDrawingRecord {
  id: string;
  title: string;
  description?: string;
  file_url: string;
  file_name?: string;
  drawing_metadata?: any;
}

const getAuthPayload = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  if (!token) {
    return null;
  }

  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const getCurrentOrganizationId = () => {
  const payload = getAuthPayload();
  return payload?.organizationId || payload?.organizations?.[0]?.organizationId;
};

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read drawing file'));
    reader.readAsDataURL(file);
  });

const normalizeDrawing = (drawing: any): SiteDrawingRecord => ({
  id: drawing.id,
  title: drawing.title,
  description: drawing.description || drawing.drawing_metadata?.siteLocation || '',
  file_url: drawing.file_url,
  file_name: drawing.file_name || drawing.title,
  drawing_metadata: drawing.drawing_metadata || {},
});

export const SiteDrawingsService = {
  getDrawings: async () => {
    const organizationId = getCurrentOrganizationId();
    const response = await ApiService.get<any>('/drawings', {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });

    const drawings = Array.isArray(response) ? response : [];
    return drawings.map(normalizeDrawing);
  },

  createDrawing: async (siteName: string, siteLocation: string, file: File) => {
    const organizationId = getCurrentOrganizationId();
    if (!organizationId) {
      throw new Error('No organization context found. Please sign in again.');
    }

    const fileUrl = await fileToDataUrl(file);
    const response = await ApiService.post<any>('/drawings', {
      title: siteName,
      description: siteLocation,
      type: 'floor_plan',
      status: 'approved',
      file_url: fileUrl,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      organization_id: organizationId,
      drawing_metadata: {
        siteLocation,
      },
    });

    return normalizeDrawing(response);
  },
};

export default SiteDrawingsService;
